// Captures console logs, network requests, and browser metadata for bug reports.
// Initialize once at app startup via `initBugReportContext()`.

const MAX_CONSOLE_ENTRIES = 50;
const MAX_NETWORK_ENTRIES = 50;

interface ConsoleEntry {
  level: "log" | "warn" | "error" | "info";
  message: string;
  timestamp: string;
}

interface NetworkEntry {
  method: string;
  url: string;
  status: number | null;
  duration: number | null;
  error?: string;
  timestamp: string;
}

const consoleLogs: ConsoleEntry[] = [];
const networkRequests: NetworkEntry[] = [];

let initialized = false;

function pushConsoleEntry(entry: ConsoleEntry) {
  consoleLogs.push(entry);
  if (consoleLogs.length > MAX_CONSOLE_ENTRIES) {
    consoleLogs.shift();
  }
}

function pushNetworkEntry(entry: NetworkEntry) {
  networkRequests.push(entry);
  if (networkRequests.length > MAX_NETWORK_ENTRIES) {
    networkRequests.shift();
  }
}

function truncate(value: unknown, maxLen = 500): string {
  try {
    const str = typeof value === "string" ? value : JSON.stringify(value);
    return str && str.length > maxLen ? str.slice(0, maxLen) + "..." : (str ?? "");
  } catch {
    return String(value);
  }
}

export function initBugReportContext() {
  if (initialized) return;
  initialized = true;

  // --- Console interception ---
  const origLog = console.log;
  const origWarn = console.warn;
  const origError = console.error;
  const origInfo = console.info;

  function wrap(level: ConsoleEntry["level"], origFn: (...args: any[]) => void) {
    return (...args: any[]) => {
      pushConsoleEntry({
        level,
        message: args.map((a) => truncate(a)).join(" "),
        timestamp: new Date().toISOString(),
      });
      origFn.apply(console, args);
    };
  }

  console.log = wrap("log", origLog);
  console.warn = wrap("warn", origWarn);
  console.error = wrap("error", origError);
  console.info = wrap("info", origInfo);

  // --- Fetch interception ---
  const origFetch = window.fetch;
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method?.toUpperCase() || "GET";
    const start = Date.now();

    try {
      const response = await origFetch.apply(window, [input, init]);
      pushNetworkEntry({
        method,
        url: truncate(url, 200),
        status: response.status,
        duration: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
      return response;
    } catch (err) {
      pushNetworkEntry({
        method,
        url: truncate(url, 200),
        status: null,
        duration: Date.now() - start,
        error: err instanceof Error ? err.message : String(err),
        timestamp: new Date().toISOString(),
      });
      throw err;
    }
  };
}

export function getBugReportContext() {
  return {
    console: [...consoleLogs],
    network: [...networkRequests],
    browser: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      screen: {
        width: screen.width,
        height: screen.height,
      },
      url: window.location.href,
      timestamp: new Date().toISOString(),
    },
  };
}
